import { AlertCircle, CheckCircle, Clock, KeyRound, ShieldAlert, XCircle } from 'lucide-react'
import type { ClientStatus } from '@common/utils/clientStatus'
import { CLIENT_STATUS_LABELS } from '@common/utils/clientStatus'
import { cn } from '@frontend/lib/utils'

const statusConfig: Record<
  ClientStatus,
  {
    variant: 'success' | 'warning' | 'error' | 'neutral'
    Icon: typeof CheckCircle
  }
> = {
  ACTIVE: { variant: 'success', Icon: CheckCircle },
  INACTIVE: { variant: 'neutral', Icon: AlertCircle },
  EXPIRED_KEY: { variant: 'error', Icon: XCircle },
  INVALID_KEY: { variant: 'error', Icon: ShieldAlert },
  KEY_EXPIRING_SOON: { variant: 'warning', Icon: Clock },
  MISSING_KEY: { variant: 'error', Icon: KeyRound },
}

const variantClasses: Record<'success' | 'warning' | 'error' | 'neutral', string> = {
  success: 'bg-green-50 text-green-700 border-green-200',
  warning: 'bg-amber-50 text-amber-700 border-amber-200',
  error: 'bg-red-50 text-red-700 border-red-200',
  neutral: 'bg-slate-100 text-slate-600 border-slate-200',
}

interface ClientStatusBadgeProps {
  status: ClientStatus
  className?: string
}

/**
 * Badge component for displaying client status.
 * Shows an icon and label with appropriate color coding.
 */
export function ClientStatusBadge({ status, className }: ClientStatusBadgeProps) {
  const { variant, Icon } = statusConfig[status]

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-medium',
        variantClasses[variant],
        className
      )}
    >
      <Icon className="h-3 w-3" />
      {CLIENT_STATUS_LABELS[status]}
    </span>
  )
}
